import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  type ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { en as mfEn, es as mfEs, fr as mfFr } from 'make-plural/plurals';
import { provideMarkdown } from 'ngx-markdown';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { GUEST_LANGUAGE_KEY } from './components/language-switcher/language-switcher';
import { errorInterceptor } from './services/error.interceptor';
import { ThemeService } from './services/theme.service';
import { tokenRefreshInterceptor } from './services/token-refresh.interceptor';
import { authInterceptor } from './services/token.interceptor';
import { TranslocoHttpLoader } from './transloco-loader';

// Function to initialize theme
function initializeTheme() {
  const themeService = inject(ThemeService);
  return () => {
    const theme = themeService.currentTheme();
    themeService.setTheme(theme);
  };
}

const SUPPORTED_LANGS = ['en', 'fr', 'es'];

function detectBrowserLanguage(): string {
  if (typeof window === 'undefined') return SUPPORTED_LANGS[0];

  // A guest's own prior choice (persisted client-side, since there's no
  // account yet) wins over the browser's language. Once logged in,
  // ContextService overrides this with the account's `language` field instead.
  const guestChoice = window.localStorage?.getItem(GUEST_LANGUAGE_KEY);
  if (guestChoice && SUPPORTED_LANGS.includes(guestChoice)) {
    return guestChoice;
  }

  const navigatorLang = window.navigator.languages?.[0] ?? window.navigator.language ?? '';
  const normalized = navigatorLang.split('-')[0]?.toLowerCase();
  return SUPPORTED_LANGS.includes(normalized) ? normalized : SUPPORTED_LANGS[0];
}

function preloadDefaultLanguage() {
  const transloco = inject(TranslocoService);
  const documentRef = inject(DOCUMENT);
  return async () => {
    const initialLang = detectBrowserLanguage();
    await firstValueFrom(transloco.load(initialLang));
    transloco.setActiveLang(initialLang);
    if (documentRef?.documentElement) {
      documentRef.documentElement.lang = initialLang;
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, tokenRefreshInterceptor, errorInterceptor]),
    ),
    provideAnimations(),
    // Provide Material snackbar module
    importProvidersFrom(MatSnackBarModule),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      multi: true,
    },
    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGS,
        defaultLang: SUPPORTED_LANGS[0],
        fallbackLang: SUPPORTED_LANGS[0],
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      locales: [mfEn, mfFr, mfEs],
    }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadDefaultLanguage,
    },
    provideClientHydration(withEventReplay()),
    provideMarkdown(),
  ],
};
